"use client";
import "./funds_sustainability.scss";
import { useFunds } from "@/contexts/funds-context";
import { Stack } from "@/elements/stack/stack";
import { Text } from "@/elements/text/text";
import { i18n } from "@/model/i18n";
import { type ReactElement } from "react";
import { Container } from "@/elements/container/container";
import { Formatter } from "../../../model/formatter";

const ACTION_FUND_EXCLUSIONS = [
  {
    label: i18n.t("pages.funds.sustainability.norm_based_assessment"),
    value: i18n.t("pages.funds.sustainability.severe_violations_ungc_oecd", {
      sup: <sup>1</sup>,
    }),
  },
  {
    label: i18n.t("pages.funds.sustainability.oil_sands_production"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 10 }),
  },
  {
    label: i18n.t("pages.funds.sustainability.fracking_production"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 10 }),
  },
  {
    label: i18n.t(
      "pages.funds.sustainability.electricity_generation_high_emissions",
    ),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 50 }),
  },
  {
    label: i18n.t("pages.funds.sustainability.oil"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 10 }),
  },
  {
    label: i18n.t("pages.funds.sustainability.other_fossil_fuels"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 10 }),
  },
  {
    label: i18n.t("pages.funds.sustainability.gas"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 50 }),
  },
  {
    label: i18n.t("pages.funds.sustainability.uranium_mining"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 10 }),
  },
  {
    label: i18n.t("pages.funds.sustainability.nuclear_power_participation"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 10 }),
  },
  {
    label: i18n.t("pages.funds.sustainability.nuclear_energy_generation"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 10 }),
  },
  {
    label: i18n.t("pages.funds.sustainability.armaments"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 10 }),
  },
  {
    label: i18n.t("pages.funds.sustainability.tobacco_production"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 5 }),
  },
];

const MIXED_FUND_EXCLUSIONS = [
  {
    label: i18n.t("pages.funds.sustainability.norm_based_assessment"),
    value: i18n.t("pages.funds.sustainability.severe_violations_ungc_oecd", {
      sup: <sup>1</sup>,
    }),
  },
  {
    label: i18n.t("pages.funds.sustainability.controversial_weapons"),
    value: i18n.t("pages.funds.sustainability.any_involvement"),
  },
  {
    label: i18n.t("pages.funds.sustainability.conventional_weapons"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 10 }),
  },
  {
    label: i18n.t("pages.funds.sustainability.coal_mining_trading"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 30 }),
  },
  {
    label: i18n.t("pages.funds.sustainability.nuclear_weapons"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 10 }),
  },
  {
    label: i18n.t("pages.funds.sustainability.tobacco_production"),
    value: i18n.t("pages.funds.sustainability.revenue", { value: 5 }),
  },
];

export function FundsSustainability(): ReactElement {
  const { selectedFund } = useFunds();
  const formatter = new Formatter({ locale: "de" });

  const mixedFund = selectedFund?.fundTypeName
    .toLowerCase()
    .includes("mischfonds");

  const EXCLUSIONS = mixedFund ? MIXED_FUND_EXCLUSIONS : ACTION_FUND_EXCLUSIONS;

  return (
    <section className="funds-sustainability" aria-label="funds-sustainability">
      <Container>
        <Stack size={40} tabletSize={80}>
          <Text type="h2-xbold" color="primary-on-default" htmlElement="h2">
            {i18n.t("pages.funds.sustainability.title")}
          </Text>
          <section
            className="funds-sustainability__section"
            aria-label="funds-sustainability-description"
          >
            <Text
              type="body-xbold"
              color="darker-pairings-primary"
              htmlElement="h3"
            >
              {i18n.t("pages.funds.sustainability.description")}
            </Text>

            <Text
              type="sh1-regular"
              color="neutral-on-surface"
              htmlElement="div"
            >
              <Stack>
                {formatter.parseText(
                  selectedFund?.sustainabilityDescription || "",
                )}
              </Stack>
            </Text>
          </section>
          <section
            className="funds-sustainability__section"
            aria-label="funds-sustainability-exclusions"
          >
            <Text
              type="body-xbold"
              color="darker-pairings-primary"
              htmlElement="h3"
            >
              {i18n.t("pages.funds.sustainability.exclusions")}
            </Text>
            <Stack>
              <ul className="funds-sustainability__table">
                {EXCLUSIONS.map((item) => {
                  return (
                    <li key={item.label} className="funds-sustainability__row">
                      <div className="funds-sustainability__cell">
                        <Text
                          type="body-regular"
                          color="neutral-on-surface"
                          htmlElement="p"
                        >
                          {item.label}
                        </Text>
                      </div>
                      <div className="funds-sustainability__cell">
                        <Text
                          type="number-snippet"
                          color="inverted-pairings-default"
                          htmlElement="p"
                        >
                          {item.value}
                        </Text>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <Stack line="mobile" size={8}>
                <Text type="caption">1</Text>
                <Text type="caption">
                  {i18n.t("pages.funds.sustainability.ungc_explanation")}
                </Text>
              </Stack>
            </Stack>
          </section>
        </Stack>
      </Container>
    </section>
  );
}
